import * as _ from 'lodash';

import { Group, IdentifierType, SelectionStrategy, IValueViewModel, IValidationViewModel, ActualDataType, DigitalSetSimpleDictionary, DigitalState, DigitalSet, Dictionary } from '../../models';
import { IIdentifierVmService } from '../';
import { IValidatedValueSelectionConfiguration } from '../../widgets';

export interface ValidatedValueSelectionObject {
    identifiers: IValidationViewModel[];
    selectableIdentifiers: IValidationViewModel[];
    childSelecteable: IValidationViewModel[];
    columns: IColumn[];
    tagIdentifiers: IValidationViewModel[];
    calculatedIdentifiers: IValidationViewModel[];
    styles: {};
}

interface IColumn {
    guid: string;
    name: string;
    object: IValidationViewModel;
}

export interface IvalidationWidgetService {
    getWidgetConfiguration(actualGroup: Group, configuration: IValidatedValueSelectionConfiguration, digitalSets: DigitalSetSimpleDictionary, viewModels: Dictionary<IValueViewModel>): ValidatedValueSelectionObject;
    getStyles(configuration, tagIdentifiers, calculatedIdentifiers, columns): { [index: string]: { width: string } };
}

export class ValidationWidgetService implements IvalidationWidgetService {
    static serviceId = 'validationWidgetService';
    static $inject = ['identifierVmService'];

    constructor(private _identifierVmService: IIdentifierVmService) {
    }

    getWidgetConfiguration(
        actualGroup: Group,
        configuration: IValidatedValueSelectionConfiguration,
        digitalSets: DigitalSetSimpleDictionary,
        viewModels: Dictionary<IValueViewModel>
    ): ValidatedValueSelectionObject {
        const validateds = actualGroup.identifierCollection
            .filter(i => i.type === IdentifierType.Validated)
            .map(i => viewModels[i.guid]);

        return this.mapViewModelsToConfig(validateds, actualGroup, configuration, digitalSets);
    }

    getStyles(
        configuration: IValidatedValueSelectionConfiguration,
        tagIdentifiers: IValidationViewModel[],
        calculatedIdentifiers: IValidationViewModel[],
        columns: IColumn[]
    ): {} {
        const dataWidth = 12;
        const tagsCount = tagIdentifiers ? tagIdentifiers.length : 0;
        const calcCount = calculatedIdentifiers ? calculatedIdentifiers.length : 0;
        const colsCount = (columns ? columns.length : 0)
            + (tagsCount + calcCount > 0 ? 1 : 0)
            + 1 //manual
            + 1;
        const remaining = 100 - colsCount * dataWidth;
        const unitRatio = 1 / 3;
        const textColsCount = 1 //name
            + (configuration.showShortComment ? 1 : 0)
            + (configuration.showUnit ? unitRatio : 0)
            + (configuration.hideComment ? 0 : 1)
            + (tagsCount + calcCount > 0 && configuration.showTagName ? 1 : 0);
        const textWidth = remaining / textColsCount;
        const nameWidth = textWidth;
        const shortCommentWidth = textWidth;
        const unitWidth = textWidth * unitRatio;
        const tagNameWidth = textWidth;
        const userCommentWidth = textWidth;
        const styles = {
            'name': { width: `${nameWidth}%` },
            'tagName': { width: `${tagNameWidth}%` },
            'unit': { width: `${unitWidth}%` },
            'shortComment': { width: `${shortCommentWidth}%` },
            'userComment': { width: `${userCommentWidth}%` },
            'data': { width: `${dataWidth}%` }
        };
        return styles;
    }

    private mapViewModelsToConfig(
        vms: IValueViewModel[],
        actualGroup: Group,
        configuration: IValidatedValueSelectionConfiguration,
        digitalSets: DigitalSetSimpleDictionary
    ): ValidatedValueSelectionObject {
        const identifiers = this.remapGroup(vms, actualGroup, digitalSets);

        const selectableIdentifiers = identifiers
            .filter(i => i.selectionStrategy === SelectionStrategy.Children);
        const mapped: IValueViewModel[] = _(selectableIdentifiers)
            .map(i => i.selectableIdentifiers)
            .flatten()
            .value() as IValueViewModel[];
        const childSelecteable = this.remapGroup(mapped, actualGroup, digitalSets);
        const columns = _(childSelecteable)
            .map(i => {
                return { guid: i.group.guid, name: i.group.name, object: i } as IColumn;
            })
            .uniqBy(g => g.guid).value();

        const tagIdentifiers = identifiers
            .filter(i => i.selectionStrategy === SelectionStrategy.Tags);
        const calculatedIdentifiers = identifiers
            .filter(i => i.selectionStrategy === SelectionStrategy.Calculated);
        const styles = this.getStyles(configuration, tagIdentifiers, calculatedIdentifiers, columns);
        return {
            identifiers,
            selectableIdentifiers,
            childSelecteable,
            columns,
            tagIdentifiers,
            calculatedIdentifiers,
            styles
        };
    }

    // presuming there is only one element in groupList which should be the case for identifiers
    private remapGroup(list: IValueViewModel[], actualGroup: Group, digitalSets: DigitalSetSimpleDictionary): IValidationViewModel[] {

        const result = list.map(original => {
            const i = Object.assign({}, original) as IValidationViewModel;
            try {
                if (i.actualDataType === ActualDataType.Digital) {
                    const set = digitalSets[i.digitalSetGuid];
                    i.digitalSet = Object.keys(set).map(k => ({ code: Number(k), name: set[k] as string }));
                }
                i.group = i.groupList[0];
                const groupIdentifier = _.find(actualGroup.identifierCollection, idt => idt.guid === i.guid);
                if (groupIdentifier) {
                    i.sortingOrder = groupIdentifier.sortingOrder;
                    // this code set valueType to string value, which prevent selection of right value afterwards
                    /*const valueType = groupIdentifier.valueType;
                    const validationValueType = ValidationValueTypes[groupIdentifier.selectionStrategy];
                    if (validationValueType) {
                        i.valueType = _.find(validationValueType, d => d.key === valueType).value;
                    }*/
                } else {
                    i.sortingOrder = 0;
                }
            } catch (e) {
                console.error(e);
            }
            return i;
        });

        return _.sortBy(result, i => i.sortingOrder);
    }
}